import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Building2, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { CATEGORY_GROUPS } from '../config/businessTypes';

export default function OnboardingWizard({ onComplete }) {
  const { token, setBusinessType, refreshBranches, API } = useAuth();
  const { lang, isAr, dir } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [loading, setLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [activeGroup, setActiveGroup] = useState('fast_food');
  const [searchQuery, setSearchQuery] = useState('');
  const l = (ar, en) => isAr ? ar : en;

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/business-types`).then(res => setBusinessTypes(res.data || [])).catch(() => {});
  }, [API]);

  const grouped = useMemo(() => {
    const groups = {};
    businessTypes.forEach(t => {
      const g = t.category_group || 'other';
      if (!groups[g]) groups[g] = [];
      groups[g].push(t);
    });
    return groups;
  }, [businessTypes]);

  const filteredTypes = useMemo(() => {
    if (!searchQuery.trim()) return grouped[activeGroup] || [];
    const q = searchQuery.toLowerCase();
    return businessTypes.filter(t =>
      t.name_ar?.includes(q) || t.name_en?.toLowerCase().includes(q) || t.id?.includes(q)
    );
  }, [searchQuery, activeGroup, grouped, businessTypes]);

  const selectedTypeData = businessTypes.find(t => t.id === selectedType);
  const accentColor = selectedTypeData?.color || '#D97706';

  const handleFinish = async () => {
    if (!selectedType || !businessName) return;
    setLoading(true);
    try {
      setBusinessType(selectedType);
      const branch = branchName || businessName;
      await axios.post(`${API}/branches`, { name: branch, subdomain: businessName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''), address: '', phone: '' }, authHeaders);
      await refreshBranches();
      toast.success(l('تم إعداد مشروعك بنجاح!', 'Your business has been set up!'));
      onComplete();
    } catch (err) {
      toast.error(l('حدث خطأ، حاول مرة أخرى', 'Something went wrong, try again'));
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 3;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" dir={dir}
      style={{ background: 'rgba(2,6,23,0.95)', backdropFilter: 'blur(12px)' }}>
      <div className="w-full max-w-2xl relative">

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 justify-center">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                style={{
                  background: step >= s ? accentColor + '20' : 'rgba(255,255,255,0.05)',
                  color: step >= s ? accentColor : 'rgba(255,255,255,0.2)',
                  border: `1.5px solid ${step >= s ? accentColor + '40' : 'rgba(255,255,255,0.08)'}`,
                }}>
                {step > s ? <Check className="w-3.5 h-3.5" /> : s}
              </div>
              {s < totalSteps && <div className="w-10 h-0.5 rounded" style={{ background: step > s ? accentColor + '40' : 'rgba(255,255,255,0.08)' }} />}
            </div>
          ))}
        </div>

        {/* Step 1: Business Type */}
        {step === 1 && (
          <div data-testid="wizard-step-1">
            <h2 className="text-xl font-extrabold text-white text-center mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {l('ما نوع مشروعك؟', 'What type is your business?')}
            </h2>
            <p className="text-sm text-white/40 text-center mb-4">{l(`اختر من ${businessTypes.length} نوع مشروع`, `Choose from ${businessTypes.length} business types`)}</p>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" style={{ [isAr ? 'right' : 'left']: 12 }} />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="wizard-type-search"
                placeholder={l('ابحث عن نوع مشروعك...', 'Search for your business type...')}
                className="w-full py-2.5 rounded-xl text-sm text-white placeholder-white/20 outline-none"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.08)', paddingRight: isAr ? 38 : 14, paddingLeft: isAr ? 14 : 38 }}
              />
            </div>

            {/* Category Tabs */}
            {!searchQuery && (
              <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1 scrollbar-hide">
                {Object.entries(CATEGORY_GROUPS).sort((a, b) => a[1].order - b[1].order).map(([key, info]) => {
                  const count = (grouped[key] || []).length;
                  return (
                    <button key={key} onClick={() => setActiveGroup(key)} data-testid={`wizard-group-${key}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all"
                      style={{
                        background: activeGroup === key ? accentColor + '15' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${activeGroup === key ? accentColor + '30' : 'rgba(255,255,255,0.06)'}`,
                        color: activeGroup === key ? accentColor : 'rgba(255,255,255,0.5)',
                      }}>
                      <span>{info.icon}</span>
                      <span>{info[lang] || info.en}</span>
                      <span className="text-[10px] opacity-60">{count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Types Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1 scrollbar-hide">
              {filteredTypes.map(type => {
                const isActive = selectedType === type.id;
                return (
                  <button key={type.id} data-testid={`wizard-type-${type.id}`}
                    onClick={() => {
                      setSelectedType(type.id);
                      if (!searchQuery) setActiveGroup(type.category_group);
                    }}
                    className="flex items-center gap-2.5 p-3 rounded-xl text-start transition-all duration-200"
                    style={{
                      background: isActive ? `${type.color}12` : 'rgba(255,255,255,0.02)',
                      border: `1.5px solid ${isActive ? `${type.color}40` : 'rgba(255,255,255,0.06)'}`,
                    }}>
                    <span className="text-lg">{type.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-xs truncate" style={{ color: isActive ? type.color : '#F8FAFC', fontFamily: 'Cairo, sans-serif' }}>
                        {isAr ? type.name_ar : type.name_en}
                      </h3>
                    </div>
                    {isActive && <Check className="w-3.5 h-3.5 shrink-0" style={{ color: type.color }} />}
                  </button>
                );
              })}
              {filteredTypes.length === 0 && (
                <div className="col-span-3 text-center py-8 text-white/30 text-sm">
                  {l('لا توجد نتائج', 'No results found')}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Business Name */}
        {step === 2 && (
          <div data-testid="wizard-step-2">
            <h2 className="text-xl font-extrabold text-white text-center mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {l('ما اسم مشروعك؟', "What's your business name?")}
            </h2>
            <p className="text-sm text-white/40 text-center mb-6">{l('الاسم الذي سيظهر لعملاؤك', 'The name your customers will see')}</p>
            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <label className="block text-xs text-white/40 mb-1.5 font-semibold">{l('اسم المشروع', 'Business Name')}</label>
                <input value={businessName} onChange={e => setBusinessName(e.target.value)} data-testid="wizard-business-name"
                  placeholder={l('مثال: مطعم الأصيل', 'Example: Al Aseel Restaurant')}
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/20 outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.08)' }}
                  onFocus={e => e.target.style.borderColor = accentColor + '50'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                />
              </div>
              <div>
                <label className="block text-xs text-white/40 mb-1.5 font-semibold">{l('اسم الفرع الأول', 'First Branch Name')}</label>
                <input value={branchName} onChange={e => setBranchName(e.target.value)} data-testid="wizard-branch-name"
                  placeholder={l('مثال: فرع الرياض (اختياري)', 'Example: Riyadh Branch (optional)')}
                  className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/20 outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.08)' }}
                  onFocus={e => e.target.style.borderColor = accentColor + '50'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div data-testid="wizard-step-3">
            <h2 className="text-xl font-extrabold text-white text-center mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {l('كل شيء جاهز!', 'All Set!')}
            </h2>
            <p className="text-sm text-white/40 text-center mb-6">{l('راجع بياناتك قبل البدء', 'Review your info before starting')}</p>
            <div className="p-5 rounded-2xl space-y-4 max-w-md mx-auto" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {selectedTypeData && (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: `${accentColor}15` }}>
                      {selectedTypeData.emoji}
                    </div>
                    <div>
                      <p className="text-xs text-white/30">{l('نوع المشروع', 'Business Type')}</p>
                      <p className="text-sm font-bold" style={{ color: accentColor }}>{isAr ? selectedTypeData.name_ar : selectedTypeData.name_en}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <Building2 className="w-5 h-5 text-white/30" />
                    </div>
                    <div>
                      <p className="text-xs text-white/30">{l('اسم المشروع', 'Business Name')}</p>
                      <p className="text-sm font-bold text-white">{businessName}</p>
                    </div>
                  </div>
                  {branchName && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
                        <Building2 className="w-5 h-5 text-white/30" />
                      </div>
                      <div>
                        <p className="text-xs text-white/30">{l('الفرع الأول', 'First Branch')}</p>
                        <p className="text-sm font-bold text-white">{branchName}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-8 max-w-md mx-auto">
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} data-testid="wizard-back-btn"
              className="flex-1 py-3 rounded-xl text-sm font-bold text-white/60 transition-all flex items-center justify-center gap-2"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              {l('رجوع', 'Back')}
            </button>
          )}
          <button
            onClick={step === totalSteps ? handleFinish : () => setStep(s => s + 1)}
            disabled={(step === 1 && !selectedType) || (step === 2 && !businessName) || loading}
            data-testid="wizard-next-btn"
            className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-30"
            style={{
              background: selectedType ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` : 'rgba(255,255,255,0.1)',
              boxShadow: selectedType ? `0 6px 20px ${accentColor}25` : 'none',
            }}>
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {l('جارِ الإعداد...', 'Setting up...')}</>
            ) : step === totalSteps ? (
              <><Sparkles className="w-4 h-4" /> {l('ابدأ الآن', 'Start Now')}</>
            ) : (
              <>{l('التالي', 'Next')} {isAr ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
